import { motion } from "motion/react";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const METRICS = [
  { number: "15 hrs/week", label: "lost to repeat review",                   accent: false },
  { number: "6–8 min/file", label: "context rebuilt by hand",                accent: false },
  { number: "~4×",          label: "document intake without adding reviewers", accent: true  },
  { number: "97%",          label: "average field accuracy on structured extraction", accent: false },
] as const;

const cardVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: EASE, delay: i * 0.1 },
  }),
};

const OperatingLeverageSection = () => (
  <section
    aria-labelledby="operating-leverage-heading"
    className="relative overflow-hidden bg-white px-6 lg:px-16 border-t border-b border-[oklch(0.88_0.008_75)]"
  >
    <div
      data-testid="operating-leverage-section-divider"
      className="absolute inset-x-0 top-0 h-px"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, oklch(0.72 0.01 75 / 0.22) 16%, oklch(var(--primary) / 0.5) 50%, oklch(0.72 0.01 75 / 0.22) 84%, transparent 100%)",
      }}
      aria-hidden="true"
    />
    <div data-testid="operating-leverage-content" className="relative max-w-7xl mx-auto py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

        {/* Left — headline */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p
            className="text-xs font-medium tracking-[0.2em] uppercase mb-5"
            style={{ color: "oklch(var(--primary))" }}
          >
            Operating Leverage
          </p>
          <h2
            id="operating-leverage-heading"
            className="font-serif font-semibold leading-[1.1] tracking-[-0.025em] text-foreground"
            style={{ fontSize: "clamp(2rem, calc(1.6rem + 1.2vw), 3rem)" }}
          >
            Scale the work,<br />
            <span data-testid="operating-leverage-heading-accent" className="text-[oklch(var(--primary))]">
              not the headcount.
            </span>
          </h2>
          <p
            className="mt-6 max-w-sm text-base font-light leading-[1.65]"
            style={{ color: "oklch(0.47 0.015 70)" }}
          >
            Run the same reviewed process across new documents while humans focus on exceptions, approvals, and decisions.
          </p>
        </motion.div>

        {/* Right — metric grid */}
        <div>
          <div className="grid grid-cols-2 gap-3">
            {METRICS.map(({ number, label, accent }, i) => (
              <motion.div
                key={label}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                data-testid="operating-leverage-metric-card"
                className="rounded-2xl border border-[oklch(0.88_0.008_75)] bg-[oklch(0.995_0.002_75)] p-5 shadow-[0_14px_40px_oklch(0.2_0.01_75_/_0.035)]"
              >
                <p
                  className="font-serif font-semibold leading-none tracking-[-0.02em]"
                  style={{
                    fontSize: "clamp(1.75rem,3vw,2.75rem)",
                    color: accent ? "oklch(var(--primary))" : "oklch(0.16 0.008 75)",
                  }}
                >
                  {number}
                </p>
                <p
                  className="mt-2.5 text-xs font-light leading-snug"
                  style={{ color: "oklch(0.47 0.015 70)" }}
                >
                  {label}
                </p>
              </motion.div>
            ))}
          </div>

          <p
            className="mt-4 text-[0.68rem] leading-relaxed"
            style={{ color: "oklch(0.55 0.012 75)" }}
          >
            Illustrative workload model. Actual results depend on volume, integrations, document quality, exception rate, and review policy. Accuracy figures are averages across structured document types.
          </p>
        </div>

      </div>
    </div>
  </section>
);

export default OperatingLeverageSection;
